import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from '@swyftx/aviary/atoms/Chip';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ThumbnailStack } from '@swyftx/aviary/atoms/ThumbnailStack';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { TransferFunds } from '@swyftx/aviary/icons/outlined';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginTransaction } from '@swyftx/margin/build/types';

import AssetIcon from '@global-components/AssetIcon/AssetIcon';

import { formatCurrency } from '@shared/utils';
import { cn } from '@shared/utils/lib/ui';

import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { getGroupByTitle } from 'src/lib/dynamic-price-alerts/utils/dynamic-price-alerts.utils';
import { useMarginTransactions } from 'src/lib/margin/hooks/transactions';
import { MarginTransactionDirection } from 'src/lib/margin/types';
import { getTransferStatusColor } from 'src/lib/margin/utils';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { MarginPositionsTableOpenedTime } from '../../MarginPositionsTable/components';
import { MarginTransactionsTableData } from '../MarginTransactionsTable.data';

type MarginPositionsHeaderData = { [key in keyof MarginTransactionsTableData]: EnhancedTableHeaderData };

type Props = {
  sortKey?: keyof MarginTransactionsTableData;
  isDesktopView?: boolean;
  isSm?: boolean;
  isXs?: boolean;
};

const useMarginTransactionsTable = ({ sortKey = 'date', isDesktopView, isSm, isXs }: Props) => {
  const [sort, setSort] = useState<EnhancedTableSort<MarginTransactionsTableData>>({
    sortKey,
    sortDirection: 'DESC',
  });

  const { t } = useTranslation('margin');
  const { getAssetByUuid } = useMarkets();
  const { transactions, isFetching, isFetchingNextPage, isLoading, hasNextPage, isError, fetchNextPage } =
    useMarginTransactions({ sortByDesc: sort.sortDirection === 'DESC', type: ['WITHDRAWAL', 'DEPOSIT'] });

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      asset: {
        title: t('tables.transfers.headers.asset'),
        alignment: 'start',
        sortable: false,
        enabled: isDesktopView,
      },

      transferFrom: {
        title: t('tables.transfers.headers.transferFrom'),
        alignment: 'start',
        sortable: false,
        frozen: false,
        showFrozenDivider: false,
        enabled: isDesktopView,
      },
      transferTo: {
        title: t('tables.transfers.headers.transferTo'),
        alignment: 'start',
        sortable: false,
        frozen: false,
        enabled: isDesktopView,
      },

      status: {
        title: t('tables.transfers.headers.status'),
        alignment: 'start',
        sortable: false,
        enabled: isDesktopView,
      },

      assetTransferStatusDate: {
        title: t('tables.transfers.headers.transferType'),
        alignment: 'start',
        sortable: false,
        enabled: !isDesktopView,
      },

      transferFromTo: {
        title: t('tables.transfers.headers.transferFromTo'),
        alignment: 'start',
        sortable: false,
        frozen: false,
        enabled: isSm,
      },

      mobileStatus: {
        title: t('tables.transfers.headers.status'),
        alignment: 'end',
        sortable: false,
        enabled: isSm,
      },

      amount: {
        title: t('tables.transfers.headers.amount'),
        alignment: 'end',
        sortable: false,
        enabled: true,
      },

      date: {
        title: t('tables.transfers.headers.date'),
        alignment: 'end',
        sortable: true,
        enabled: isDesktopView,
        showFrozenDivider: false,
      },
    }),
    [isDesktopView, isSm, t],
  );

  const data: EnhancedTableData<MarginTransactionsTableData, MarginTransaction>[] = useMemo(
    () =>
      transactions?.map((transaction: MarginTransaction) => {
        const asset = getAssetByUuid(transaction.assetId);
        const transactionFailed = transaction.status === 'FAILED';
        const transferFrom =
          transaction.type === 'DEPOSIT' ? MarginTransactionDirection.Spot : MarginTransactionDirection.Derivatives;
        const transferTo =
          transaction.type === 'DEPOSIT' ? MarginTransactionDirection.Derivatives : MarginTransactionDirection.Spot;

        return {
          value: transaction,
          asset: {
            value: transaction.assetId,
            element: (
              <FlexLayout spacing={8} alignItems='center'>
                <AssetIcon asset={asset} size={20} />
                <Body color='primary' size='small'>
                  {asset?.code}
                </Body>
              </FlexLayout>
            ),
          },

          transferFrom: {
            value: transferFrom,
            element: (
              <Body size='small' className='whitespace-nowrap' color='primary'>
                {t(`transfer.${transferFrom}`, { asset: asset?.code })}
              </Body>
            ),
          },
          transferTo: {
            value: transferTo,
            element: (
              <Body size='small' className='whitespace-nowrap' color='primary'>
                {t(`transfer.${transferTo}`, { asset: asset?.code })}
              </Body>
            ),
          },

          status: {
            value: transaction.status,
            element: (
              <Chip color={getTransferStatusColor(transaction.status)} variant='subtle' size='sm'>
                {capitalize(transaction.status)}
              </Chip>
            ),
          },

          assetTransferStatusDate: {
            value: transaction.assetId,
            groupByTitle: getGroupByTitle(DateTime.fromMillis(transaction.timestamp)),
            element: (
              <FlexLayout spacing={12} alignItems='center' className='ml-8'>
                <ThumbnailStack
                  size='md'
                  thumbnails={[
                    {
                      type: 'icon',
                      icon: TransferFunds,
                      appearance: 'circle',
                      bgColor: 'bg-color-background-surface-secondary',
                    },
                    {
                      type: 'asset',
                      hasPadding: false,
                      appearance: 'circle',
                      assetCode: asset?.code,
                    },
                  ]}
                />

                <FlexLayout direction='column' spacing={4}>
                  <Body color='primary' weight='bold'>
                    {t(`transfer.transferTo.${transferTo}`)}
                  </Body>

                  <FlexLayout alignItems='center' spacing={4}>
                    {isXs ? (
                      <>
                        <Body size='small' color='secondary'>
                          {capitalize(transaction.status)}
                        </Body>
                        <Body size='small' color='secondary'>
                          •
                        </Body>
                      </>
                    ) : null}
                    <Body size='small' color='secondary' className='whitespace-nowrap'>
                      {transaction.timestamp ? DateTime.fromMillis(transaction.timestamp).toFormat('h:mma') : '-'}
                    </Body>
                  </FlexLayout>
                </FlexLayout>
              </FlexLayout>
            ),
          },

          transferFromTo: {
            value: transferTo,
            element: (
              <FlexLayout direction='column'>
                <Body color='primary' weight='emphasis'>
                  {t(`transfer.${transferFrom}`, { asset: asset?.code })}
                </Body>
                <Body size='small' color='secondary'>
                  {t(`transfer.${transferTo}`, { asset: asset?.code })}
                </Body>
              </FlexLayout>
            ),
          },

          mobileStatus: {
            value: transaction.status,
            element: (
              <FlexLayout justifyContent='end' alignItems='center'>
                <Chip color={getTransferStatusColor(transaction.status)} variant='subtle' size='sm'>
                  {capitalize(transaction.status)}
                </Chip>
              </FlexLayout>
            ),
          },

          amount: {
            value: transaction.amount,
            element: (
              <FlexLayout direction='column' justifyContent='start' alignItems='end' spacing={4}>
                <Numeric
                  color={transactionFailed ? 'placeholder' : 'primary'}
                  className={cn('whitespace-nowrap', transactionFailed ? 'line-through' : '')}
                  weight={isDesktopView ? 'none' : 'bold'}
                  size='medium'
                >
                  {formatCurrency(transaction.amount, asset, { appendCode: true, hideCode: false })}
                </Numeric>
              </FlexLayout>
            ),
          },

          date: {
            value: transaction.timestamp,
            groupByTitle: getGroupByTitle(DateTime.fromMillis(transaction.timestamp)),
            element: (
              <FlexLayout justifyContent='end'>
                <MarginPositionsTableOpenedTime value={transaction.timestamp} />
              </FlexLayout>
            ),
          },
        };
      }),
    [getAssetByUuid, isDesktopView, isXs, t, transactions],
  );

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginTransactionsTableData>) => {
      const tableData: EnhancedTableData<MarginTransactionsTableData, MarginTransaction>[] = Object.assign([], data);

      if (!newSort) return tableData;

      setSort(newSort);
    },
    [data],
  );

  return {
    initialSort: sort,
    headers,
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    isError,
    onSort,
  };
};

export { useMarginTransactionsTable };
